import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import {
  useUserGroupsQuery,
  useCohortBoardQuery,
  useUserGroupMembersBulkQuery,
} from '@/hooks/queries';
import { useSessionState } from '@/hooks/useSessionState';
import { useLocalFilter } from '@/hooks/useLocalFilter';
import { SessionKey } from '@/types/storage';
import { SearchInput } from '@/components/core';
import { useThemeColors } from '@/styles';
import { withOpacity } from '@/styles/colors/withOpacity';
import { spacing, radius, sizing, typography } from '@/styles/design-tokens';
import { useToast } from '@/hooks/useToast';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { QueryError } from '@/lib/execute-query';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import type { WeekInfo } from '@/utils/week';
import { Icon, IconName } from '@/icons';
import { CohortBoardView } from './CohortBoardView';
import { AsyncButton } from '@/components/ui';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: SYNC_COHORTS + board creation wired to local state machine; toast handled inline
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import type {
  SyncCohortsInput,
  SyncCohortsOutput,
  GetBoardTemplatesInput,
  GetBoardTemplatesOutput,
  CreateBoardFromTemplateInput,
  CreateBoardFromTemplateOutput,
} from '@/types/generated';
import type {
  GetBoardTemplatesTemplatesItem,
  GetUserGroupsGroupsItem,
} from '@/types/generated/processor-types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COHORT_GROUP_SEARCH_KEYS: Array<keyof GetUserGroupsGroupsItem> = [
  'name',
  'description',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CohortsProps {
  selectedWeek: WeekInfo;
  onGroupChange?: (group: { name: string } | null) => void;
  onSyncStateChange?: (
    syncFn: (() => Promise<void>) | null,
    isSyncing: boolean
  ) => void;
  onBoardIdChange?: (boardId: string | null) => void;
  /** When set (e.g. from a deep link), skip group selection and show this board directly */
  initialBoardId?: string;
}

// ---------------------------------------------------------------------------
// Main Cohorts screen
// ---------------------------------------------------------------------------

export default function Cohorts({
  selectedWeek,
  onGroupChange,
  onSyncStateChange,
  onBoardIdChange,
  initialBoardId,
}: CohortsProps) {
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();
  const toast = useToast();

  const [selectedGroupId, setSelectedGroupId] = useSessionState<string | null>(
    SessionKey.LB_COHORTS_SELECTED_GROUP_ID,
    null
  );
  const { data: groupsData, isLoading: isGroupsLoading } =
    useUserGroupsQuery(activeOrganizationId);
  const allGroups = groupsData?.groups ?? [];

  // Only show groups where all members share the same role
  const groupIds = allGroups.map(g => g.id);
  const {
    data: membersData,
    isLoading: isMembersLoading,
    isError: isMembersError,
    isSuccess: isMembersSuccess,
    refetch: refetchMembers,
  } = useUserGroupMembersBulkQuery(groupIds);

  const membersByGroup = new Map<string, { role: string }[]>();
  membersData?.members.forEach(m => {
    const list = membersByGroup.get(m.group_id) ?? [];
    list.push(m);
    membersByGroup.set(m.group_id, list);
  });

  // While the eligibility check is loading or has errored, don't drop groups
  // from the list — only exclude a group once we've confirmed it has zero
  // (or mixed-role) members.
  const groups = isMembersSuccess
    ? allGroups.filter(g => {
        const members = membersByGroup.get(g.id);
        if (!members?.length) return false;
        const roles = members.map(m => m.role);
        return roles.every(r => r === roles[0]);
      })
    : allGroups;

  const isEligibilityLoading = allGroups.length > 0 && isMembersLoading;

  // Local search — filters data already in frontend state, no extra API call
  const [groupSearch, setGroupSearch] = useState('');
  const filteredGroups = [
    ...useLocalFilter(groups, groupSearch, COHORT_GROUP_SEARCH_KEYS),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const {
    data: cohortBoardData,
    isLoading: isLoadingBoard,
    error: cohortBoardError,
  } = useCohortBoardQuery(
    !initialBoardId
      ? {
          group_id: selectedGroupId ?? undefined,
          organization_id: activeOrganizationId ?? undefined,
        }
      : { group_id: undefined, organization_id: undefined }
  );

  const boardId = initialBoardId ?? cohortBoardData?.board_id ?? null;
  const boardNotFound =
    !initialBoardId &&
    !!selectedGroupId &&
    cohortBoardError instanceof QueryError &&
    (cohortBoardError.code === ResponseCode.NOT_FOUND ||
      cohortBoardError.code === ResponseCode.RESOURCE_NOT_FOUND);

  useEffect(() => {
    if (
      cohortBoardError &&
      !(
        cohortBoardError instanceof QueryError &&
        (cohortBoardError.code === ResponseCode.NOT_FOUND ||
          cohortBoardError.code === ResponseCode.RESOURCE_NOT_FOUND)
      )
    ) {
      toast.error('Failed to load cohort board');
    }
  }, [cohortBoardError]);

  const [refreshCounter, setRefreshCounter] = useState(0);

  const { execute: executeSync, isLoading: isSyncing } = useOrchestrator<
    SyncCohortsInput,
    SyncCohortsOutput
  >(Action.SYNC_COHORTS);

  // Notify parent whenever boardId changes
  useEffect(() => {
    onBoardIdChange?.(boardId);
  }, [boardId]);

  const handleSync = useCallback(async () => {
    if (!boardId) return;
    const result = await executeSync({
      board_id: boardId,
      week_number: selectedWeek.week,
      year: selectedWeek.year,
    });
    if (result.code === ResponseCode.SUCCESS && result.data) {
      const { fanned_out, synced } = result.data;
      toast.success(`Synced: ${fanned_out} new, ${synced} updated`);
      setRefreshCounter(c => c + 1);
    } else {
      toast.error('Sync failed');
    }
  }, [boardId, selectedWeek, executeSync, toast]);

  const handleSyncRef = useRef(handleSync);
  handleSyncRef.current = handleSync;

  useEffect(() => {
    onSyncStateChange?.(
      boardId ? () => handleSyncRef.current() : null,
      isSyncing
    );
  }, [boardId, isSyncing]);

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  useEffect(() => {
    onGroupChange?.(selectedGroup ? { name: selectedGroup.name } : null);
  }, [selectedGroup?.name]);

  if (!user) return null;

  return (
    <div
      className="flex h-full overflow-hidden"
      style={{ backgroundColor: colors.background }}
    >
      {/* ── Left panel: group list ─────────────────────────── */}
      <div
        className="flex-shrink-0 flex flex-col overflow-y-auto border-r"
        style={{
          width: '240px',
          borderColor: colors.border.default,
          backgroundColor: colors.background,
        }}
      >
        <div style={{ padding: spacing.md }}>
          <div
            className="font-semibold text-xs uppercase tracking-wide"
            style={{ color: colors.text.secondary, marginBottom: spacing.sm }}
          >
            Groups
          </div>
          {groups.length > 0 && (
            <div style={{ marginBottom: spacing.sm }}>
              <SearchInput
                value={groupSearch}
                onChange={setGroupSearch}
                placeholder="Search groups..."
              />
            </div>
          )}
          {isMembersError && (
            <div
              className="flex items-center justify-between gap-2"
              style={{
                color: colors.text.secondary,
                fontSize: typography.sizes.xs,
                padding: spacing.sm,
                marginBottom: spacing.sm,
                borderRadius: radius.sm,
                backgroundColor: withOpacity(colors.text.tertiary, 0.08),
              }}
            >
              <span>Couldn&apos;t check group eligibility.</span>
              <button
                type="button"
                onClick={() => refetchMembers()}
                style={{ color: colors.text.secondary, fontWeight: 600 }}
              >
                Retry
              </button>
            </div>
          )}
          {isGroupsLoading || isEligibilityLoading ? (
            <div
              style={{
                color: colors.text.tertiary,
                fontSize: typography.sizes.xs,
                padding: spacing.sm,
              }}
            >
              Loading...
            </div>
          ) : groups.length === 0 ? (
            <div
              className="flex flex-col gap-1"
              style={{
                color: colors.text.tertiary,
                fontSize: typography.sizes.xs,
                padding: spacing.sm,
              }}
            >
              <span style={{ color: colors.text.secondary, fontWeight: 600 }}>
                {allGroups.length === 0
                  ? 'No groups yet'
                  : 'No eligible groups'}
              </span>
              <span>
                {allGroups.length === 0
                  ? 'Create a group from the Groups tab and add members to get started.'
                  : 'Groups need all members to share the same role before they appear here.'}
              </span>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div
              style={{
                color: colors.text.tertiary,
                fontSize: typography.sizes.xs,
                padding: spacing.sm,
              }}
            >
              No groups match "{groupSearch}"
            </div>
          ) : (
            filteredGroups.map(group => (
              <CohortGroupListItem
                key={group.id}
                group={group}
                isSelected={selectedGroupId === group.id}
                onClick={() => setSelectedGroupId(group.id)}
                colors={colors}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right panel ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {initialBoardId && boardId ? (
          // Deep-link entry: render board directly, no group selection needed
          <CohortBoardView
            boardId={boardId}
            selectedWeek={selectedWeek}
            refreshTrigger={refreshCounter}
          />
        ) : !selectedGroupId ? (
          <div
            className="flex-1 flex flex-col items-center justify-center gap-2"
            style={{ color: colors.text.secondary }}
          >
            <Icon name={IconName.USERS} size="xl" />
            <div style={{ fontSize: typography.sizes.sm, fontWeight: 600 }}>
              Select a group to manage assignments
            </div>
            <div
              style={{
                fontSize: typography.sizes.xs,
                color: colors.text.tertiary,
                textAlign: 'center',
                maxWidth: '240px',
              }}
            >
              Choose a group from the left panel to view or create its cohort
              board.
            </div>
          </div>
        ) : isLoadingBoard ? (
          <div
            className="flex-1 flex items-center justify-center"
            style={{ color: colors.text.secondary }}
          >
            Loading board...
          </div>
        ) : boardNotFound && selectedGroupId ? (
          <CreateCohortBoardPanel
            groupId={selectedGroupId}
            groupName={selectedGroup?.name ?? ''}
            onCreated={newBoardId => {
              if (selectedGroupId && activeOrganizationId) {
                queryClient.setQueryData(
                  queryKeys.cohortBoard.forGroup(
                    selectedGroupId,
                    activeOrganizationId
                  ),
                  { board_id: newBoardId, board_title: '', is_new: true }
                );
              }
            }}
          />
        ) : boardId ? (
          <CohortBoardView
            boardId={boardId}
            selectedWeek={selectedWeek}
            refreshTrigger={refreshCounter}
          />
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CohortGroupListItem
// ---------------------------------------------------------------------------

function CohortGroupListItem({
  group,
  isSelected,
  onClick,
  colors,
}: {
  group: { id: string; name: string; member_count: number };
  isSelected: boolean;
  onClick: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const bgColor = isSelected
    ? colors.active.background
    : isHovered
      ? withOpacity(colors.primary, 0.12)
      : 'transparent';

  const borderColor = isSelected
    ? colors.active.background
    : isHovered
      ? colors.primary
      : 'transparent';

  return (
    <button
      className="w-full text-left rounded-lg transition-all duration-150"
      style={{
        padding: `${spacing.sm} ${spacing.md}`,
        marginBottom: spacing.xs,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        fontSize: typography.sizes.sm,
        fontWeight: isSelected ? 600 : 400,
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          color: isSelected ? colors.active.text : colors.text.primary,
        }}
      >
        {group.name}
      </div>
      <div
        style={{
          color: isSelected
            ? withOpacity(colors.active.text, 0.85)
            : colors.text.secondary,
          fontSize: typography.sizes.xs,
        }}
      >
        {group.member_count} members
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// CreateCohortBoardPanel — shown when a group has no board yet
// ---------------------------------------------------------------------------

function CreateCohortBoardPanel({
  groupId,
  groupName,
  onCreated,
}: {
  groupId: string;
  groupName: string;
  onCreated: (boardId: string) => void;
}) {
  const colors = useThemeColors();
  const { user } = useAuth();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [templates, setTemplates] = useState<GetBoardTemplatesTemplatesItem[]>(
    []
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [boardTitle, setBoardTitle] = useState(
    groupName ? `${groupName} Board` : 'Cohort Board'
  );
  const [isCreating, setIsCreating] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    const result = await actionOrchestrator.execute<
      GetBoardTemplatesInput,
      GetBoardTemplatesOutput
    >(Action.GET_BOARD_TEMPLATES, {} as GetBoardTemplatesInput);
    setIsLoadingTemplates(false);
    if (result.code === ResponseCode.SUCCESS && result.data) {
      setTemplates(result.data.templates);
      if (result.data.templates.length === 1) {
        setSelectedTemplateId(result.data.templates[0].id);
      }
    }
  }, []);

  useEffect(() => {
    if (showForm) fetchTemplates();
  }, [showForm, fetchTemplates]);

  const handleCreate = async () => {
    if (!user || !selectedTemplateId || !boardTitle.trim()) return;
    setIsCreating(true);
    const result = await actionOrchestrator.execute<
      CreateBoardFromTemplateInput,
      CreateBoardFromTemplateOutput
    >(Action.CREATE_BOARD_FROM_TEMPLATE, {
      template_id: selectedTemplateId,
      board_title: boardTitle.trim(),
      owner_id: user.id,
      group_id: groupId,
    });
    setIsCreating(false);
    if (result.code === ResponseCode.SUCCESS && result.data) {
      toast.success('Cohort board created');
      onCreated(result.data.board_id);
    } else {
      toast.error('Failed to create cohort board');
    }
  };

  if (!showForm) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center gap-4"
        style={{ color: colors.text.secondary }}
      >
        <Icon name={IconName.KANBAN} size="xl" />
        <div className="text-center">
          <div
            style={{
              fontSize: typography.sizes.md,
              fontWeight: 600,
              color: colors.text.primary,
              marginBottom: spacing.xs,
            }}
          >
            No cohort board yet
          </div>
          <div style={{ fontSize: typography.sizes.sm }}>
            Create a board to manage assignments for this group
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 font-medium transition-all"
          style={{
            backgroundColor: colors.primary,
            color: colors.text.inverse,
            padding: `${spacing.sm} ${spacing.md}`,
            borderRadius: radius.DEFAULT,
            fontSize: typography.sizes.sm,
          }}
        >
          <Icon name={IconName.PLUS} size="sm" color={colors.text.inverse} />
          Create Cohort Board
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center"
      style={{ padding: spacing.xl }}
    >
      <div
        className="w-full max-w-md"
        style={{
          backgroundColor: colors.background,
          border: `1px solid ${colors.border.default}`,
          borderRadius: radius.lg,
          padding: spacing.xl,
        }}
      >
        <h3
          className="font-semibold text-lg mb-4"
          style={{ color: colors.text.primary }}
        >
          Create Cohort Board
        </h3>

        {/* Board title */}
        <div style={{ marginBottom: spacing.md }}>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: colors.text.primary }}
          >
            Board Title
          </label>
          <input
            type="text"
            value={boardTitle}
            onChange={e => setBoardTitle(e.target.value)}
            className="w-full px-3"
            style={{
              backgroundColor: colors.offWhite,
              border: `1px solid ${colors.border.default}`,
              borderRadius: radius.md,
              height: sizing.input.md,
              color: colors.text.primary,
              outline: 'none',
            }}
          />
        </div>

        {/* Template picker */}
        <div style={{ marginBottom: spacing.lg }}>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: colors.text.primary }}
          >
            Template
          </label>
          {isLoadingTemplates ? (
            <div
              style={{
                color: colors.text.tertiary,
                fontSize: typography.sizes.sm,
              }}
            >
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div
              style={{
                color: colors.text.secondary,
                fontSize: typography.sizes.sm,
              }}
            >
              No templates available. Ask your admin to create one first.
            </div>
          ) : (
            <select
              value={selectedTemplateId}
              onChange={e => setSelectedTemplateId(e.target.value)}
              className="w-full px-3"
              style={{
                backgroundColor: colors.offWhite,
                border: `1px solid ${colors.border.default}`,
                borderRadius: radius.md,
                height: sizing.input.md,
                color: selectedTemplateId
                  ? colors.text.primary
                  : colors.text.secondary,
                outline: 'none',
              }}
            >
              <option value="">Select a template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setShowForm(false)}
            disabled={isCreating}
            style={{
              color: colors.text.secondary,
              fontSize: typography.sizes.sm,
              padding: `${spacing.sm} ${spacing.md}`,
            }}
          >
            Cancel
          </button>
          <AsyncButton
            onClick={handleCreate}
            disabled={!selectedTemplateId || !boardTitle.trim()}
            isLoading={isCreating}
            loadingText="Creating..."
          >
            Create Board
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}
